import React, {
  useState,
  ErrorInfo,
  ComponentType,
  HtmlHTMLAttributes,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import clsx from 'clsx';
import { styled, SxProps } from '@mui/material/styles';
import { CoreLayoutProps } from 'ra-core';

import { AppBar as DefaultAppBar, AppBarProps } from 'react-admin';
import { Sidebar as DefaultSidebar, SidebarProps } from 'react-admin';
import { Menu as DefaultMenu, MenuProps } from 'react-admin';
import { Error, ErrorProps } from 'react-admin';
import { SkipNavigationButton } from 'react-admin';
import { useSidebarState } from 'react-admin';
import { Inspector } from 'react-admin';
import CustomAppBar from './CustomAppBar';
import CustomUserMenu from './CustomUserMenu';
import CustomMenu from './CustomMenu';
import CustomFooter from './CustomFooter';

const CustomLayout = (props: LayoutProps) => {
  const {
      appBar: AppBar = DefaultAppBar,
      appBarAlwaysOn = true,
      children,
      className,
      dashboard,
      error: errorComponent,
      menu: Menu = DefaultMenu,
      sidebar: Sidebar = DefaultSidebar,
      title,
      ...rest
  } = props;

  const [open] = useSidebarState();
  const [errorInfo, setErrorInfo] = useState<ErrorInfo>();

  const handleError = (error: Error, info: ErrorInfo) => {
      setErrorInfo(info);
  };

  return (
      <StyledLayout className={clsx('layout', className)} {...rest}>
          <SkipNavigationButton />
          <div className={LayoutClasses.appFrame}>
              <CustomAppBar toolbar={<></>} open={open} title={title} alwaysOn={appBarAlwaysOn} userMenu={<CustomUserMenu />} />
              <main className={LayoutClasses.contentWithSidebar}>
                  <Sidebar appBarAlwaysOn={appBarAlwaysOn}>
                    <CustomMenu hasDashboard={!!dashboard} />
                  </Sidebar>
                  <div id="main-content" className={LayoutClasses.content}>
                      <ErrorBoundary
                          onError={handleError}
                          fallbackRender={({ error, resetErrorBoundary }) => (
                              <Error
                                  error={error}
                                  errorComponent={errorComponent}
                                  errorInfo={errorInfo}
                                  resetErrorBoundary={resetErrorBoundary}
                                  title={title}
                              />
                          )}
                      >
                          {children}
                      </ErrorBoundary>
                  </div>
              </main>
              <Inspector />
          </div>
          <CustomFooter />
      </StyledLayout>
  );
};

export interface LayoutProps
  extends CoreLayoutProps,
      Omit<HtmlHTMLAttributes<HTMLDivElement>, 'title'> {
  appBar?: ComponentType<AppBarProps>;
  appBarAlwaysOn?: boolean;
  className?: string;
  error?: ComponentType<ErrorProps>;
  menu?: ComponentType<MenuProps>;
  sidebar?: ComponentType<SidebarProps>;
  sx?: SxProps;
}

export interface LayoutState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

const PREFIX = 'RaLayout';
export const LayoutClasses = {
  appFrame: `${PREFIX}-appFrame`,
  contentWithSidebar: `${PREFIX}-contentWithSidebar`,
  content: `${PREFIX}-content`,
};

const StyledLayout = styled('div', {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root,
})(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1,
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
  position: 'relative',
  minWidth: 'fit-content',
  width: '100%',
  color: theme.palette.getContrastText(theme.palette.background.default),

  [`& .${LayoutClasses.appFrame}`]: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      marginBottom: theme.spacing(7),
      marginTop: theme.spacing(6),
      [theme.breakpoints.down('sm')]: {
          marginTop: theme.spacing(7),
      },
  },
  [`& .${LayoutClasses.contentWithSidebar}`]: {
      display: 'flex',
      flexGrow: 1,
      transition: theme.transitions.create('margin', {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
      }),
  },
  [`& .${LayoutClasses.content}`]: {
      backgroundColor: theme.palette.background.default,
      zIndex: 2,
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      flexBasis: 0,
      padding: 0,
      [theme.breakpoints.up('xs')]: {
          paddingRight: theme.spacing(1),
          paddingLeft: theme.spacing(1),
      },
  },
}));

export default CustomLayout